import { useEffect, useState } from 'react';
import { faArrowsRepeat } from '@fortawesome/pro-regular-svg-icons/faArrowsRepeat';
import { faStar } from '@fortawesome/pro-solid-svg-icons/faStar';
import { useTranslation } from 'react-i18next';
import { createSearchParams, generatePath, useNavigate, useSearchParams } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import Cookies from 'universal-cookie';

import { availabilityTypeConstant, bookingTypeConstant, bookingVerticalType } from 'us-react-components/dist/constants/Booking';
import { isMobile } from 'us-react-components/dist/utils/viewport';
import { SimpleWidgetCard } from 'us-react-components/dist/SimpleWidgetCard/SimpleWidgetCard';
import {
  AgencyAvailabilityService,
  AgencyBookingRateService,
  Booking,
  BookingService,
  Job,
  JobService,
  ParentSearchBar,
  ParentSignupVerticalType,
  ParentSignupVerticalTypeId,
  Referral,
  ReferralOffer,
  ReferralOfferService,
  SavedSearchService,
  SavedSearch,
  SignupReasonCode,
  Facility,
  Sitter,
  SitterService,
  KinsideService,
  UserGeocode,
  UserGeocodeService,
  KinsideSearchParams,
} from 'us-web-services';
import { Storage } from '../../constants';
import { useDependencies } from '../../dependencies';
import { ArticlesAndResources } from '../../components/Parent/Dashboard/components/widgets/ArticlesAndResources';
import { SafetyTrust } from '../../components/Parent/Dashboard/components/widgets/SafetyTrust';
import { staticUrls } from '../../constants/StaticUrls';
import { getScheduleTypeFromAvailability } from '../../utils';
import { Routes } from '../../Routes';
import { SideBar } from './SideBar';
import { MainContainer } from './MainContainer';

const OCCASIONAL_AVAILABILITY_ID = availabilityTypeConstant.find((a: { id?: number; }) => a.id === 3)?.id;

export const Dashboard = observer((): JSX.Element => {
  document.title = 'UrbanSitter - Dashboard';
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { stores } = useDependencies();

  const isMobileView = isMobile();

  const { parent } = stores.parent;
  const { user } = stores.user;

  const [activeBookings, setActiveBookings] = useState<Booking[]>([]);
  const [activeJobs, setActiveJobs] = useState<Job[]>([]);
  const [bestMatchSitters, setBestMatchSitters] = useState<Sitter[]>([]);
  const [bestMatchFacilities, setBestMatchFacilities] = useState<Facility[]>([]);
  const [geocode, setGeocode] = useState<UserGeocode>({});
  const [isAgencyCareAvailable, setIsAgencyCareAvailable] = useState<boolean>(false);
  const [loadingBestMatch, setLoadingBestMatch] = useState(true);
  const [openJobCount, setOpenJobCount] = useState<number>(0);
  const [referral, setReferral] = useState<Referral>({});
  const [referralOffer, setReferralOffer] = useState<ReferralOffer>({});
  const [search, setSearch] = useState<ParentSearchBar>({});
  const [searchParams] = useSearchParams();
  const cookies = new Cookies();
  const [parentSavedSearches, setParentSavedSearches] = useState<SavedSearch[]>([]);

  const getActiveBookings = async (): Promise<void> => {
    const config = {
      params: {
        parentId: user.id,
        userId: user.id,
        limit: 4,
        filterType: 'all_active',
      },
    };

    const response = (await BookingService.getByFilter(config)).data;

    setActiveBookings(response.data);
  };

  const getSavedSearches = async (): Promise<void> => {
    const config = {
      params: {
        active: true,
        parentId: user.id
      }
    };
    const response = (await SavedSearchService.getSavedSearchByFilter(config)).data;

    setParentSavedSearches(response.data);
  };

  const getActiveJobs = async (): Promise<void> => {
    const config = {
      params: {
        limit: 2,
        status: 'open',
        parentId: user.id,
        userId: user.id,
      },
    };

    const response = (await JobService.getByFilter(config)).data;

    setActiveJobs(response.data);
    setOpenJobCount(response.meta.size);
  };

  const getWillSitParams = (newSearch: ParentSearchBar): object => {
    let params = {};
    switch (newSearch.vertical?.id) {
      case bookingVerticalType.SENIOR_CARE:
        return {
          willSitSenior: Storage.TRUE,
        };
      case bookingVerticalType.HOUSEHOLD_SERVICES:
        return {
          willSitHome: Storage.TRUE,
        };
      case bookingVerticalType.PET_CARE:
        if (parent.dog) {
          params = {
            ...params,
            willSitDog: Storage.TRUE,
          };
        }
        if (parent.cat) {
          params = {
            ...params,
            willSitCat: Storage.TRUE,
          };
        }
        if (parent.otherPet) {
          params = {
            ...params,
            willSitOtherPet: Storage.TRUE,
          };
        }
        return params;
      default:
        return {};
    };
  };

  const getSitterParams = (newSearch: ParentSearchBar): object => {
    const originalParams = {
      userId: user.id,
      parentId: user.id,
      city: geocode?.city,
      zip: newSearch.zip,
      state: geocode?.state ?? parent?.address?.state,
      distance: newSearch.distance ?? 25,
      exactRecurringAvailability: false,
      latitude: geocode?.latitude,
      longitude: geocode?.longitude,
      numberOfChildren: parent?.kids?.length ?? 1,
      page: 1,
      verticalTypeId: newSearch.vertical?.id ?? bookingVerticalType.CHILD_CARE,
      availabilityType: availabilityTypeConstant.find((a: { id?: number; }) => a.id === newSearch.searchType?.id)?.type ?? 'All',
      searchTypeCode: availabilityTypeConstant.find((a: { id?: number; }) => a.id === newSearch.searchType?.id)?.code ?? 'ALL',
      excludeFavorites: true,
    };

    const additionalParams = getWillSitParams(newSearch);

    return {
      ...originalParams,
      ...additionalParams,
    };
  };

  const getUserGeocode = async (zip: string): Promise<void> => {
    const id = user.id || 0;

    const config = {
      id,
      zip,
    };

    try {
      const response = (await UserGeocodeService.create(id, config)).data;

      if (response.data) {
        sessionStorage.setItem(Storage.GEOCODE_DATA_KEY, JSON.stringify(response.data));
        await setGeocode(response.data);
      } else {
        await setGeocode({});
      }
    } catch {
      await setGeocode({});
      setBestMatchSitters([]);
      setBestMatchFacilities([]);
    }
  };

  const getBestMatch = async (newSearch: ParentSearchBar): Promise<void> => {
    setLoadingBestMatch(true);

    const params = getSitterParams(newSearch);
    const config = {
      params: {
        limit: 6,
        ...params,
      },
    };

    try {
      const sittersResponse = (await SitterService.getByFilter(config)).data.data;
      setBestMatchSitters(sittersResponse);
    } catch (e) {
      setBestMatchSitters([]);
      console.warn(e);
    }

    if (newSearch.vertical?.id === bookingVerticalType.CHILD_CARE || newSearch.vertical?.id === bookingVerticalType.DAY_CARE) {
      if (!geocode.latitude || !geocode.longitude) {
        console.warn('Cannot search for facilities without valid geolocation data');
        return;
      }

      const fullPageLimit = isMobileView ? 8 : 12;

      const facilitySearchParams : KinsideSearchParams = {
        latitude: geocode.latitude,
        longitude: geocode.longitude,
        distance: newSearch.distance ?? 25,
        limit: fullPageLimit,
        scheduleTypes: getScheduleTypeFromAvailability(newSearch.searchType?.id)?.toString()
      }

      try {
        const facilitiesResponse = (await KinsideService.search(facilitySearchParams)).data.data;
        setBestMatchFacilities(facilitiesResponse);
      } catch (e) {
        setBestMatchFacilities([]);
        console.warn(e);
      }
    }

    setLoadingBestMatch(false);
  };

  const initSearchBar = async (): Promise<void> => {
    let searchBarData: ParentSearchBar = {};

    try {
      searchBarData = JSON.parse(sessionStorage.getItem(Storage.SEARCH_BAR_DATA_KEY) || '{}');
    } catch {
      searchBarData = {};
    }

    if (!searchBarData.userId || !searchBarData.zip) {
      const parentVerticals = parent?.signupVerticalTypes || [];
      const wantsChildcare = !parentVerticals.length
        || parentVerticals?.some(
          (psvt: ParentSignupVerticalType) => psvt.signupVerticalType?.id === ParentSignupVerticalTypeId.CHILD_CARE);

      let verticalTypeId = bookingVerticalType.CHILD_CARE;
      if (!wantsChildcare) {
        const wantsPetCare = parentVerticals?.some(
          (psvt: ParentSignupVerticalType) => [
            ParentSignupVerticalTypeId.PET_CARE_CAT,
            ParentSignupVerticalTypeId.PET_CARE_DOG,
            ParentSignupVerticalTypeId.PET_CARE_OTHER].includes(psvt.signupVerticalType!.id));
        const wantsSeniorCare = parentVerticals?.some(
          (psvt: ParentSignupVerticalType) => psvt.signupVerticalType?.id === ParentSignupVerticalTypeId.SENIOR_CARE);

        if (wantsSeniorCare) {
          verticalTypeId = bookingVerticalType.SENIOR_CARE;
        } else if (wantsPetCare) {
          verticalTypeId = bookingVerticalType.PET_CARE;
        } else { // only selected Household Services
          verticalTypeId = bookingVerticalType.HOUSEHOLD_SERVICES;
        }
      }

      searchBarData = {
        userId: user.id,
        parentId: user.id,
        zip: parent?.address?.zip,
        distance: 25,
        vertical: {
          id: verticalTypeId,
        }
      };

      // Set initial search frequency based on signup reason, if available
      if (parent.signupReason?.reason) {
        switch (parent.signupReason.reason.code) {
          case SignupReasonCode.one_time:
            searchBarData.searchType = { id: 3 };
            break;

          case SignupReasonCode.full_time:
            searchBarData.searchType = { id: 2 };
            break;

          case SignupReasonCode.part_time:
            searchBarData.searchType = { id: 1 };
            break;

          default:
            searchBarData.searchType = undefined; // "All" frequency
            break;
        }
      }

      sessionStorage.setItem(Storage.SEARCH_BAR_DATA_KEY, JSON.stringify(searchBarData));
    }

    await setSearch(searchBarData);

    if (searchBarData?.zip) {
      await getUserGeocode(searchBarData.zip);
    }
  };

  const isRelativeUrl = (url: string): boolean => /^\/[a-z0-9]/.test(url);

  const searchChanged = async (newSearch: ParentSearchBar): Promise<void> => {
    const updatedSearch = {
      ...newSearch,
      userId: user.id,
      parentId: user.id,
    };

    sessionStorage.setItem(Storage.SEARCH_BAR_DATA_KEY, JSON.stringify(updatedSearch));

    if (updatedSearch.zip && updatedSearch.zip !== search.zip) {
      // If zip changed, reset and get new geocode before getting best matches
      await setGeocode({});
      await setSearch(updatedSearch);
      getUserGeocode(updatedSearch.zip);
    } else {
      await setSearch(updatedSearch);
      getBestMatch(newSearch);
    }
  };

  const handlePostAJob = (): void => {
    navigate({
      pathname: generatePath(Routes.parent.jobPost),
      search: `${createSearchParams({
        bookingType: bookingTypeConstant.find((b: { id: number; }) => b.id === search.vertical?.id)?.code ?? '',
        verticalTypeId: (search.vertical?.id ?? bookingVerticalType.CHILD_CARE).toString(),
        zip: search.zip ?? '',
      })}`,
    });
  };

  const addSkipCookie = (): void => {
    const expires = new Date();

    expires.setMinutes(expires.getMinutes() + 60);
    cookies.set(Storage.SKIP_BENEFITS_CHECK, Storage.TRUE, {
      expires,
      path: '/',
    });
  };

  const checkAndRedeemReferral = async (): Promise<void> => {
    const branchStorage = localStorage.getItem(Storage.BRANCH_DATA_KEY);
    let branchData;

    try {
      branchData = branchStorage ? JSON.parse(branchStorage) : false;

      if (branchData && branchData.parentReferrerId) {
        const params = {
          invitedUserId: user.id,
          referringUserId: branchData.parentReferrerId,
        };

        const response = (await ReferralOfferService.redeem(params)).data;

        if (response.data?.referringUserId && response.data?.invitedUserId) {
          setReferral(response.data);

          const offer = (await ReferralOfferService.get(response.data.referringUserId, response.data.invitedUserId)).data;
          if (offer.data) {
            setReferralOffer(offer.data);
            localStorage.removeItem(Storage.BRANCH_DATA_KEY);
          }
        }
      }
    } catch (error) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      if ((error as any).response?.data?.errors?.pop()?.code === 'referral.duplicate') {
        // remove referral data from localStorage, offer already redeemed
        localStorage.removeItem(Storage.BRANCH_DATA_KEY);
      }
      // suppress any other errors, e.g. JSON parse error
    }
  };

  const isPendingEmployee = (): boolean => {
    const companyEmail = localStorage.getItem(Storage.COMPANY_EMAIL);
    const companyEmployee = localStorage.getItem(Storage.COMPANY_EMPLOYEE);

    return !!(companyEmail || companyEmployee);
  };

  const benefitsRedirect = (): boolean => {
    const skipBenefitsCheck = cookies.get(Storage.SKIP_BENEFITS_CHECK);

    if (searchParams.get('token')) {
      navigate(generatePath(Routes.parent.benefits.activate));

      return true;
    }

    if (searchParams.get('skipBenefits')) {
      addSkipCookie();

      return false;
    }

    if (isPendingEmployee() && !skipBenefitsCheck) {
      const socialConfirmed = cookies.get(Storage.SOCIAL_CONFIRMED);
      const socialSignup = user?.facebookId || user?.googleUserId;

      const newPath = (socialSignup && !socialConfirmed) ? Routes.parent.registration.confirm : Routes.parent.benefits.confirm;
      navigate(generatePath(newPath));

      return true;
    }

    return false;
  };

  const checkAgencyCareAvailability = async (): Promise<void> => {
    const availabiliyFilter = {
      params: {
        zipCode: search.zip,
      },
    };


    if (!search.zip || search.vertical?.id !== bookingVerticalType.CHILD_CARE || (search?.searchType?.id && search.searchType.id !== OCCASIONAL_AVAILABILITY_ID)) {
      setIsAgencyCareAvailable(false);

      return;
    }

    try {
      const agencyAvailabilities = (await AgencyAvailabilityService.getByFilter(availabiliyFilter)).data;

      if (agencyAvailabilities.data.length > 0) {
        const bookingRateFilter = {
          params: {
            zip: search.zip,
          },
        };
        const agencyRates = (await AgencyBookingRateService.getByFilter(bookingRateFilter)).data;

        setIsAgencyCareAvailable(agencyRates.data.length > 0);
      } else {
        setIsAgencyCareAvailable(false);
      }
    } catch {
      setIsAgencyCareAvailable(false);
    }
  };

  const cookieRedirect = (): boolean => {
    const redirectTo = cookies.get(Storage.REDIRECT_URL);

    if (redirectTo) {
      cookies.remove(Storage.REDIRECT_URL, { path: '/' });

      if (isRelativeUrl(redirectTo)) {
        navigate(redirectTo.replace(/^\/app\//, '/'));

        return true;
      }
    }

    return false;
  };

  useEffect(() => {
    if (benefitsRedirect()) {
      return;
    }

    if (cookieRedirect()) {
      return;
    }

    localStorage.removeItem(Storage.UUID);

    if (stores.parent.hasValidAddress()) {
      checkAndRedeemReferral();
      initSearchBar();
      getActiveBookings();
      getSavedSearches();
      getActiveJobs();
    } else if (!isPendingEmployee()) {
      if (parent.signupReason?.id || cookies.get(Storage.JUST_BROWSING)) {
        navigate(Routes.parent.registration.location);
      } else {
        navigate(Routes.parent.registration.gettingStarted);
      }
    } else {
      navigate(Routes.parent.registration.trustSafety);
    }
  }, []);

  // Update best matches and agency care availability when geocode data changes
  useEffect(() => {
    if (geocode.zip && search.zip) {
      getBestMatch(search);
      checkAgencyCareAvailability();
    }
  }, [geocode]);

  useEffect(() => {
    checkAgencyCareAvailability();
  }, [search]);

  return (
    <article className='container py-3'>
      <div className='w-full'>
        <div className='flex flex-col md:flex-row md:mt-10'>
          <SideBar
            activeBookings={activeBookings}
            activeJobs={activeJobs}
            geocode={geocode}
            handlePostAJob={handlePostAJob}
            isAgencyCareAvailable={isAgencyCareAvailable}
            openJobCount={openJobCount}
            parentSavedSearches={parentSavedSearches}
            referralOffer={referralOffer}
            referral={referral} />
          <MainContainer
            bestMatchFacilities={bestMatchFacilities}
            bestMatchSitters={bestMatchSitters}
            geocode={geocode}
            getWillSitParams={getWillSitParams}
            handlePostAJob={handlePostAJob}
            isAgencyCareAvailable={isAgencyCareAvailable}
            loadingBestMatch={loadingBestMatch}
            searchChanged={searchChanged}
            search={search} />
        </div>
      </div>
      <div className='w-full'>
        <div className='flex flex-col md:gap-4 space-y-5 md:space-y-0 md:flex-row h-fit md:mb-20'>
          <div className='flex md:hidden lg:hidden xl:flex w-full md:w-96 md:h-full'>
            <SafetyTrust />
          </div>
          <div className='w-full md:h-48'>
            <SimpleWidgetCard
              iconNames={[faStar, faStar, faStar, faStar, faStar]}
              iconClasses='text-honey'
              title={t('dashboard.widgets.fiveStar.title')}
              description={t('dashboard.widgets.fiveStar.description')}
            />
          </div>
          <div className='w-full md:h-48'>
            <SimpleWidgetCard
              iconNames={[faArrowsRepeat]}
              title={t('dashboard.widgets.repeatFamilies.title')}
              description={t('dashboard.widgets.repeatFamilies.description')}
            />
          </div>
          <div className='w-full md:h-48'>
            <SimpleWidgetCard
              pictureUrl={staticUrls.familyTestimonal}
              description={t('dashboard.widgets.recommendation.description')}
              moreDescription={t('dashboard.widgets.recommendation.subDescription')}
            />
          </div>
        </div>
        <div className='w-full flex mt-10'>
          <ArticlesAndResources />
        </div>
      </div>
    </article>
  );
});
